import { useCallback, useEffect, useState } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';

import {
  AdminIntegrationsPage_IntegrationConfigurationFragment,
  useUpdateIntegrationConfigurationMutation,
} from '../../graphql/AdminIntegrationsPage.generated';

/** Configuration flags that turn on a Snowflake sync. */
type SnowflakeSyncField =
  | 'snowflakeDataSharingEnabled'
  | 'snowflakeHouseholdSyncEnabled';

export interface UseSnowflakeSyncToggleArgs {
  configuration: AdminIntegrationsPage_IntegrationConfigurationFragment | null;
  field: SnowflakeSyncField;
  /** Name of the sync, used in the success and error messages. */
  description: string;
}

export function useSnowflakeSyncToggle({
  configuration,
  field,
  description,
}: UseSnowflakeSyncToggleArgs) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const persistedValue = configuration?.[field] ?? false;
  const [enabled, setEnabled] = useState(persistedValue);
  const [updateConfiguration, { loading: isUpdating }] =
    useUpdateIntegrationConfigurationMutation();

  useEffect(() => {
    setEnabled(persistedValue);
  }, [persistedValue]);

  const toggle = useCallback(async () => {
    if (!configuration) {
      throw new Error('Configuration is not available');
    }

    const nextEnabledState = !enabled;

    try {
      await updateConfiguration({
        variables: {
          input: {
            id: configuration.id,
            update: {
              [field]: nextEnabledState,
            },
          },
        },
      });
      setEnabled(nextEnabledState);
      showFeedback(`${description} updated successfully`, {
        variant: 'success',
      });
    } catch (err) {
      showFeedback(`Failed to update ${description} configuration`);
      const error = err instanceof Error ? err : undefined;
      reportError(`Failed to update ${description} configuration`, error);
    }
  }, [
    configuration,
    description,
    enabled,
    field,
    reportError,
    showFeedback,
    updateConfiguration,
  ]);

  return { enabled, isUpdating, toggle };
}
